import { useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import Magnetic from '../components/animations/Magnetic';
import SpotlightCard from '../components/animations/SpotlightCard';
import SimpleHeroSection from '@/components/simpleHero';
import { 
  Sparkles, Layout, Code, Zap, Cpu, 
  ShoppingCart, Smartphone, Cloud, Package, HeadphonesIcon,
  ClipboardCheck, Users as UsersIcon, MessageSquare, ArrowRight,
  GraduationCap, Globe, Rocket, CheckCircle,
  Target, Award, Shield, Clock, BarChart, Users
} from 'lucide-react';
import './Services.css';

const services = [
  {
    id: 1,
    number: "01",
    title: "Web & Full Stack Development",
    description: "Modern, scalable web applications with complete frontend, backend, and cloud integration.",
    icon: Code,
    highlights: ["React / Next.js", "Backend Systems", "Database Design", "Cloud Deployment"],
    moreInfo: "Dashboards, SaaS apps, admin panels, business tools, and full-stack platforms."
  },
  {
    id: 2,
    number: "02",
    title: "Mobile App Development",
    description: "High-performance Android and iOS applications with clean UI and real-time capabilities.",
    icon: Smartphone,
    highlights: ["Android & iOS", "Clean UI", "Real-time Data", "Cloud Integration"],
    moreInfo: "Apps with authentication, payments, notifications, and IoT integrations."
  },
  {
    id: 3,
    number: "03",
    title: "E-Commerce Solutions",
    description: "Complete e-commerce platforms with secure payments and advanced admin controls.",
    icon: ShoppingCart,
    highlights: ["Payment Integration", "Inventory Management", "Customer Accounts", "Analytics"],
    moreInfo: "Online stores with cart systems, tracking, admin dashboards, and business insights."
  },
  {
    id: 4,
    number: "04",
    title: "SaaS & Business Platforms",
    description: "Scalable SaaS products with automation, dashboards, and subscription systems.",
    icon: Cloud,
    highlights: ["Scalable Architecture", "Authentication", "Automation", "Data Storage"],
    moreInfo: "Cloud-based tools, business workflows, analytics systems, and subscription platforms."
  },
  {
    id: 5,
    number: "05",
    title: "IoT & Smart Solutions",
    description: "Real-time IoT systems with hardware, cloud connectivity, and automation logic.",
    icon: Cpu,
    highlights: ["Real-time Systems", "Cloud Connectivity", "Embedded Hardware", "Dashboards"],
    moreInfo: "Solutions using ESP32, Arduino, Raspberry Pi with smart automation and monitoring."
  },
  {
    id: 6,
    number: "06",
    title: "Student & Innovation Solutions",
    description: "End-to-end support for projects, hackathons, and professional portfolios.",
    icon: GraduationCap,
    highlights: ["Final Year Projects", "Hackathons", "Portfolios", "Complete Support"],
    moreInfo: "Working models, source code, documentation, and guidance for students and creators."
  }
];

const features = [
  {
    id: 1,
    title: "Expert Team",
    description: "Highly skilled professionals with industry experience and technical expertise.",
    icon: Users
  },
  {
    id: 2,
    title: "Quality Assurance",
    description: "Rigorous testing and quality checks ensuring flawless project delivery.",
    icon: Award
  },
  {
    id: 3,
    title: "Timely Delivery",
    description: "Adherence to deadlines with efficient project management and agile methodologies.",
    icon: Clock
  },
  {
    id: 4,
    title: "Innovative Solutions",
    description: "Cutting-edge technologies and creative approaches for unique challenges.",
    icon: Target
  },
  {
    id: 5,
    title: "Client Collaboration",
    description: "Transparent communication and regular updates throughout the development process.",
    icon: Shield
  },
  {
    id: 6,
    title: "Performance Focus",
    description: "Optimized solutions ensuring speed, scalability, and excellent user experience.",
    icon: BarChart
  }
];

const processSteps = [
  {
    id: 1,
    number: "01",
    title: "Consultation",
    description: "Detailed discussions to understand your requirements, goals, and project timeline.",
    icon: MessageSquare
  },
  {
    id: 2,
    number: "02",
    title: "Planning",
    description: "Comprehensive project plan with milestones, technologies, and deliverables.",
    icon: ClipboardCheck
  },
  {
    id: 3,
    number: "03",
    title: "Development",
    description: "Agile development with regular updates, testing, and quality assurance.",
    icon: Code
  },
  {
    id: 4,
    number: "04",
    title: "Testing",
    description: "Rigorous testing across all scenarios to ensure flawless performance.",
    icon: CheckCircle
  },
  {
    id: 5,
    number: "05",
    title: "Deployment",
    description: "Smooth deployment with documentation, training, and support handover.",
    icon: Rocket
  },
  {
    id: 6,
    number: "06",
    title: "Support",
    description: "Ongoing maintenance, updates, and support for long-term success.",
    icon: UsersIcon
  }
];

const Services = () => {
  const gridRef = useRef(null);
  const isInView = useInView(gridRef, { once: true, margin: '-50px' });

  return (
    <motion.div
      className="services-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {/* Hero Section */}
      <SimpleHeroSection
        title="Our Services"
        tagline="Comprehensive solutions tailored to your technical needs"
      /> 

      {/* Services List Section */}
      <section className="services-list">
        <div className="services-list-container">
          <div className="services-list-header">
            <motion.h2 
              className="services-list-title"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              What We Offer
            </motion.h2>
            <motion.p 
              className="services-list-subtitle"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Comprehensive services designed to meet your unique needs and exceed expectations
            </motion.p>
          </div>
          
          <div ref={gridRef} className="services-list-grid">
            <AnimatePresence>
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <Magnetic key={service.id} strength={0.2}>
                    <motion.div
                      initial={{ opacity: 0, y: 60, scale: 0.95 }}
                      animate={isInView ? { 
                        opacity: 1, 
                        y: 0, 
                        scale: 1,
                        transition: {
                          duration: 0.6,
                          delay: index * 0.05,
                          ease: "backOut"
                        }
                      } : {}}
                      whileHover={{ 
                        y: -5,
                        transition: { duration: 0.3, ease: "easeOut" }
                      }}
                    >
                      <SpotlightCard className="service-list-spotlight">
                        <div className="service-list-card">
                          <div className="service-list-card-number">{service.number}</div>
                          <motion.div 
                            className="service-list-icon"
                            whileHover={{ scale: 1.05 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          >
                            <Icon size={40} />
                          </motion.div>
                          <h3 className="service-list-title">{service.title}</h3>
                          <p className="service-list-description">{service.description}</p>
                          
                          <div className="service-list-highlights">
                            {service.highlights.map((highlight, idx) => (
                              <span key={idx} className="service-list-highlight">
                                {highlight}
                              </span>
                            ))}
                          </div>
                          
                          <motion.div 
                            className="service-more-info"
                            initial={{ opacity: 0, height: 0 }}
                            whileHover={{ opacity: 1, height: "auto" }}
                            transition={{ duration: 0.4 }}
                          >
                            <div className="service-more-divider" />
                            <p className="service-know-more">
                              <strong>Details:</strong> {service.moreInfo}
                            </p>
                          </motion.div>
                        </div>
                      </SpotlightCard>
                    </motion.div>
                  </Magnetic>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Process Section - Vertical Timeline */}
      <section className="process-section">
        <div className="process-container">
          <div className="process-header">
            <motion.h2 
              className="process-title"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Our Process
            </motion.h2>
            <motion.p 
              className="process-subtitle"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              A structured approach ensuring quality, transparency, and successful project delivery
            </motion.p>
          </div>
          
          <div className="process-timeline-wrapper">
            <div className="process-timeline-line"></div>
            <div className="process-timeline-cards">
              {processSteps.map((step, index) => {
                const StepIcon = step.icon;
                const isLeft = index % 2 === 0;
                return (
                  // <Magnetic key={step.id} strength={0.2}>
                    <motion.div
                      className={`process-timeline-card ${isLeft ? 'process-card-left' : 'process-card-right'}`}
                      initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: '-100px' }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                    >
                      <SpotlightCard className="process-card-content">
                      <span className="process-number">{step.number}</span>
                      <div className="process-icon-wrapper">
                        <StepIcon className="process-icon" size={36} />
                      </div>
                      <h3 className="process-card-title">{step.title}</h3>
                      <p className="process-card-description">{step.description}</p>
                    </SpotlightCard>
                    <div className="process-timeline-node"></div>
                    <div className="process-timeline-connector"></div>
                    </motion.div>
                  // </Magnetic>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
          <motion.div
            className="contact-cta"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3>Have a project in mind?</h3>
            <p>Let's discuss how we can bring your technical vision to life.</p>
            <Magnetic strength={0.2}>
              <motion.a
                href="/contact"
                className="cta-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start a Project
              </motion.a>
            </Magnetic>
          </motion.div>
    </motion.div>
  );
};

export default Services;